/**
 * Auto Repair SEO Page
 * 
 * Dynamic SEO landing pages for auto repair services by city and service type.
 * URLs: /autoremont/{city}, /autoremont/{city}/{service}
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage, useAuth } from '@/lib/context';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Car, Wrench, MapPin, Star, Phone, Clock, ArrowRight, Search, 
  ChevronRight, Shield, CheckCircle2, Building2, Menu
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Service types with i18n keys
const SERVICE_TYPES = [
  { value: 'oil_change', labelKey: 'auto.services.oilChange', icon: '🛢️' },
  { value: 'brake_repair', labelKey: 'auto.services.brakeRepair', icon: '🔧' },
  { value: 'tire_service', labelKey: 'auto.services.tireService', icon: '🛞' },
  { value: 'engine_repair', labelKey: 'auto.services.engineRepair', icon: '⚙️' },
  { value: 'body_work', labelKey: 'auto.services.bodyWork', icon: '🚗' },
  { value: 'electrical', labelKey: 'auto.services.electrical', icon: '⚡' },
  { value: 'diagnostics', labelKey: 'auto.services.diagnostics', icon: '🔍' },
  { value: 'ac_service', labelKey: 'auto.services.acService', icon: '❄️' },
  { value: 'transmission', labelKey: 'auto.services.transmission', icon: '🔄' },
  { value: 'inspection', labelKey: 'auto.services.inspection', icon: '✅' }
];

// Cities for each country
const CITIES_BY_COUNTRY = {
  estonia: ['tallinn', 'tartu', 'parnu', 'narva', 'kohtla-jarve'],
  finland: ['helsinki', 'espoo', 'tampere', 'vantaa', 'oulu', 'turku'],
  sweden: ['stockholm', 'gothenburg', 'malmo', 'uppsala', 'vasteras'],
  norway: ['oslo', 'bergen', 'trondheim', 'stavanger', 'drammen']
};

// Detect country from city
const getCountryFromCity = (city) => {
  for (const [country, cities] of Object.entries(CITIES_BY_COUNTRY)) {
    if (cities.includes(city.toLowerCase())) return country;
  }
  return null;
};

export default function AutoRepairSeoPage() {
  const { city, service } = useParams();
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const normalizedCity = city?.toLowerCase();
  const normalizedService = service?.toLowerCase();
  const country = getCountryFromCity(normalizedCity);

  // Fetch workshops for this city/service
  useEffect(() => {
    const fetchWorkshops = async () => {
      setLoading(true);
      try {
        let url = `${API}/api/auto/services?city=${normalizedCity}`;
        if (normalizedService) {
          url += `&service_type=${normalizedService}`;
        }
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          setWorkshops(data.services || []);
        }
      } catch (error) {
        console.error('Failed to fetch workshops:', error);
      } finally {
        setLoading(false);
      }
    };

    if (normalizedCity) {
      fetchWorkshops();
    }
  }, [normalizedCity, normalizedService]);

  // Format city name for display
  const formatCityName = (citySlug) => {
    if (!citySlug) return '';
    return citySlug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join('-');
  };

  // Get SEO data based on language
  const getSeoData = () => {
    const cityName = formatCityName(normalizedCity);
    const serviceType = SERVICE_TYPES.find(s => s.value === normalizedService);
    const serviceName = serviceType ? t(serviceType.labelKey) : null;

    if (serviceName) {
      return {
        title: t('seo.autoRepair.titleWithService', { city: cityName, service: serviceName }),
        description: t('seo.autoRepair.descWithService', { city: cityName, service: serviceName }),
        h1: t('seo.autoRepair.h1WithService', { city: cityName, service: serviceName })
      };
    }
    
    return {
      title: t('seo.autoRepair.title', { city: cityName }),
      description: t('seo.autoRepair.desc', { city: cityName }),
      h1: t('seo.autoRepair.h1', { city: cityName })
    };
  };

  const seo = getSeoData();
  const cityName = formatCityName(normalizedCity);

  // Build canonical URL
  const canonicalUrl = normalizedService 
    ? `https://keikka.com/autoremont/${normalizedCity}/${normalizedService}`
    : `https://keikka.com/autoremont/${normalizedCity}`;

  return (
    <div className="min-h-screen bg-background">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{seo.title} | Keikka</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
        {/* Hreflang tags for multi-language */}
        <link rel="alternate" hreflang="en" href={`https://keikka.com/en/auto-repair/${normalizedCity}${normalizedService ? `/${normalizedService}` : ''}`} />
        <link rel="alternate" hreflang="et" href={`https://keikka.com/et/autoremont/${normalizedCity}${normalizedService ? `/${normalizedService}` : ''}`} />
        <link rel="alternate" hreflang="fi" href={`https://keikka.com/fi/autokorjaamo/${normalizedCity}${normalizedService ? `/${normalizedService}` : ''}`} />
        <link rel="alternate" hreflang="x-default" href={canonicalUrl} />
      </Helmet>

      {/* Simple Navigation */}
      <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-2xl font-bold text-brand">KEIKKA</Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/jobs" className="text-muted-foreground hover:text-foreground transition">
                {t('nav.jobs')}
              </Link>
              <Link to="/auto" className="text-muted-foreground hover:text-foreground transition">
                {t('nav.autoRepair')}
              </Link>
              <Link to="/rentals" className="text-muted-foreground hover:text-foreground transition">
                {t('nav.rentals')}
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-foreground">{t('common.home')}</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to="/auto" className="hover:text-foreground">{t('nav.autoRepair')}</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-foreground">{cityName}</span>
          {normalizedService && (
            <>
              <ChevronRight className="h-4 w-4" />
              <span className="text-foreground">
                {SERVICE_TYPES.find(s => s.value === normalizedService)?.icon} {t(SERVICE_TYPES.find(s => s.value === normalizedService)?.labelKey || normalizedService)}
              </span>
            </>
          )}
        </nav>

        {/* Hero Section */}
        <section className="mb-12">
          <div className="bg-gradient-to-r from-brand/10 to-blue-500/10 rounded-2xl p-8 md:p-12">
            <div className="flex items-center gap-2 text-brand mb-4">
              <MapPin className="h-5 w-5" />
              <span className="font-medium">{cityName}</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              {seo.h1}
            </h1>
            <p className="text-lg text-muted-foreground mb-6 max-w-2xl">
              {seo.description}
            </p>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2 text-sm">
                <Shield className="h-4 w-4 text-green-600" />
                <span>{t('auto.seo.verifiedWorkshops')}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span>{t('auto.seo.freeQuotes')}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Star className="h-4 w-4 text-yellow-500" />
                <span>{t('auto.seo.customerReviews')}</span>
              </div>
            </div>

            {/* Quick Search */}
            <div className="flex flex-col sm:flex-row gap-3 max-w-xl">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('auto.seo.searchPlaceholder')}
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Button 
                size="lg" 
                onClick={() => navigate(`/services/request?city=${normalizedCity}&category=auto`)}
                data-testid="request-quote-btn"
              >
                {t('auto.seo.requestQuote')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>

        {/* Service Types Grid (if no specific service selected) */}
        {!normalizedService && (
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6">{t('auto.seo.popularServices')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {SERVICE_TYPES.map((serviceType) => (
                <Link
                  key={serviceType.value}
                  to={`/autoremont/${normalizedCity}/${serviceType.value}`}
                  className="flex flex-col items-center p-4 rounded-xl border hover:border-brand hover:bg-brand/5 transition-all group"
                  data-testid={`service-${serviceType.value}`}
                >
                  <span className="text-3xl mb-2">{serviceType.icon}</span>
                  <span className="text-sm font-medium text-center group-hover:text-brand">
                    {t(serviceType.labelKey)}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Workshops List */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">
              {t('auto.seo.workshopsInCity', { city: cityName, count: workshops.length })}
            </h2>
            <Badge variant="outline">{workshops.length} {t('auto.seo.results')}</Badge>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-6 bg-muted rounded w-3/4 mb-4"></div>
                    <div className="h-4 bg-muted rounded w-1/2 mb-2"></div>
                    <div className="h-4 bg-muted rounded w-2/3"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : workshops.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-medium mb-2">{t('auto.seo.noWorkshopsYet')}</h3>
                <p className="text-muted-foreground mb-4">
                  {t('auto.seo.noWorkshopsDesc', { city: cityName })}
                </p>
                <Button onClick={() => navigate('/services/request?category=auto')}>
                  {t('auto.seo.postRequest')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {workshops.map((workshop) => (
                <Card 
                  key={workshop.id} 
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/auto/${workshop.id}`)}
                  data-testid={`workshop-${workshop.id}`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{workshop.workshop_name}</CardTitle>
                        <CardDescription className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {workshop.city}
                        </CardDescription>
                      </div>
                      {workshop.rating && (
                        <Badge className="bg-yellow-100 text-yellow-800">
                          <Star className="h-3 w-3 mr-1 fill-current" />
                          {workshop.rating.toFixed(1)}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Services offered */}
                    <div className="flex flex-wrap gap-1 mb-4">
                      {workshop.service_types?.slice(0, 3).map((type) => (
                        <Badge key={type} variant="secondary" className="text-xs">
                          {SERVICE_TYPES.find(s => s.value === type)?.icon} {t(SERVICE_TYPES.find(s => s.value === type)?.labelKey || type)}
                        </Badge>
                      ))}
                      {workshop.service_types?.length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{workshop.service_types.length - 3}
                        </Badge>
                      )}
                    </div>
                    
                    {/* Quick Info */}
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      {workshop.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {t('auto.seo.callAvailable')}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {t('auto.seo.quickResponse')}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Related Cities */}
        {country && (
          <section className="mb-12">
            <h2 className="text-xl font-bold mb-4">{t('auto.seo.otherCities')}</h2>
            <div className="flex flex-wrap gap-2">
              {CITIES_BY_COUNTRY[country]
                .filter(c => c !== normalizedCity)
                .map((otherCity) => (
                  <Link
                    key={otherCity}
                    to={`/autoremont/${otherCity}${normalizedService ? `/${normalizedService}` : ''}`}
                    className="px-4 py-2 rounded-full border hover:border-brand hover:bg-brand/5 transition-all text-sm"
                  >
                    {formatCityName(otherCity)}
                  </Link>
                ))}
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="bg-brand/5 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold mb-2">{t('auto.seo.ctaTitle')}</h2>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            {t('auto.seo.ctaDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              size="lg" 
              onClick={() => navigate(`/services/request?city=${normalizedCity}&category=auto`)}
            >
              {t('auto.seo.getQuotes')}
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => navigate('/auto')}
            >
              {t('auto.seo.browseAll')}
            </Button>
          </div>
        </section>
      </main>

      {/* Simple Footer */}
      <footer className="border-t mt-12 py-8">
        <div className="container mx-auto px-4 text-center text-muted-foreground">
          <p>© {new Date().getFullYear()} Keikka. {t('footer.allRightsReserved')}</p>
        </div>
      </footer>
    </div>
  );
}
